import * as React from 'react';

import { useIsFetching } from '@tanstack/react-query';
import { translate } from '~l10n/client';

import { Divider } from '~shared/components/Divider';
import { QuizList, useColumnSortReducer } from '~teacher/components/quizzes';
import { DeletedQuizUpgradeBanner } from '~teacher/components/quizzes/DeletedPanel/DeletedQuizUpgradeBanner';
import { usePurgeQuizItems } from '~teacher/components/quizzes/DeletedPanel/usePurgeQuizItems';
import { PurgeDialog } from '~teacher/components/quizzes/PurgeDialog';
import { useFilteredSortedQuizListItems } from '~teacher/components/quizzes/QuizList/useFilteredSortedQuizListItems';
import { getDeletedQuizFilter } from '~teacher/components/quizzes/QuizListFilters';
import {
  TrashFolderActionProvider,
  TrashQuizActionProvider,
} from '~teacher/components/quizzes/QuizListItem/ActionProviders';
import { handleRestoreSelected } from '~teacher/components/quizzes/QuizTrashActions';
import { QuizTrashActions } from '~teacher/components/quizzes/QuizTrashActions/QuizTrashActions';
import { VirtualQuizFolderList } from '~teacher/components/quizzes/VirtualQuizFolderList';
import { useQuizzes } from '~teacher/hook';
import { useRestoreQuizFolder } from '~teacher/hook/useRestoreQuizFolder';
import { sortQuizListItems } from '~teacher/pages/sortQuizListItems';

import { QuizOrFolder } from '../../../../api/quiz/getQuizzes/types';

export function DeletedPanel({ paywallDispatch, quizLimitPaywallDispatch }) {
  const [
    { direction: sortDirection, quizListColumn: sortProperty },
    dispatchColumnSortAction,
  ] = useColumnSortReducer();

  const sortQuizItems = React.useCallback(
    (items: QuizOrFolder[]): QuizOrFolder[] =>
      sortQuizListItems({
        items: items,
        sortProperty: sortProperty,
        sortDirection: sortDirection,
      }),
    [sortProperty, sortDirection]
  );

  const quizzes = useQuizzes();
  const isFetching = useIsFetching();
  const isLoading = isFetching > 0;

  const {
    toggleSelectAll,
    isSelectedAll,
    selectNone,
    selectedQuizItem,
    toggleItem,
    quizOrFolders,
  } = useFilteredSortedQuizListItems({
    quizListItems: quizzes.data,
    filter: getDeletedQuizFilter(),
    sort: sortQuizItems,
  });

  const { mutate: restoreQuizFolder } = useRestoreQuizFolder(
    [...selectedQuizItem],
    paywallDispatch,
    quizLimitPaywallDispatch
  );

  const onRestoreFolder = (folderId: number) => {
    restoreQuizFolder({
      folderIds: [folderId],
      parentId: null,
      paywallDispatch: paywallDispatch,
      quizLimitPaywallDispatch: quizLimitPaywallDispatch,
    });
  };

  const onRestoreQuiz = (quizId: number) => {
    restoreQuizFolder({
      quizIds: [quizId],
      parentId: null,
      paywallDispatch: paywallDispatch,
      quizLimitPaywallDispatch: quizLimitPaywallDispatch,
    });
  };
  const {
    isOpen: isPurgeDialogOpen,
    confirmAction: purgeDialogConfirmAction,
    selectedItemCount,
    handleOpenPurgeDialog,
    onPurgeFolder,
    onPurgeQuiz,
    handleClosePurgeDialog,
  } = usePurgeQuizItems(selectedQuizItem, selectNone);

  const { mutate: restoreQuizFolderMutation } = useRestoreQuizFolder(
    [...selectedQuizItem],
    paywallDispatch,
    quizLimitPaywallDispatch
  );

  const restoreSelected = handleRestoreSelected({
    selectedQuizItem: selectedQuizItem,
    restoreQuizFolderMutation,
  });

  return (
    <TrashFolderActionProvider
      onRestoreFolder={onRestoreFolder}
      onPurgeFolder={onPurgeFolder}
    >
      <TrashQuizActionProvider
        onRestoreQuiz={onRestoreQuiz}
        onPurgeQuiz={onPurgeQuiz}
      >
        <DeletedQuizUpgradeBanner />

        <QuizList
          isDeleted
          selectedAll={isSelectedAll}
          onChangeSelect={toggleSelectAll}
          sortDirection={sortDirection}
          sortProperty={sortProperty}
          dispatchColumnSortAction={dispatchColumnSortAction}
          selectedQuizItem={selectedQuizItem}
          actions={
            <QuizTrashActions
              selectedQuizItems={selectedQuizItem}
              restoreSelected={restoreSelected}
              openPurgeDialog={handleOpenPurgeDialog}
            />
          }
        >
          {quizOrFolders.length === 0 ? (
            <>
              <div className="p-16 flex justify-center content-center text-md font-display text-blue-900">
                {translate('The trash is empty')}
              </div>
              <Divider />
            </>
          ) : (
            <VirtualQuizFolderList
              selectedQuizItem={selectedQuizItem}
              toggleItem={toggleItem}
              items={quizOrFolders}
              disableHover
            />
          )}
        </QuizList>
        <PurgeDialog
          selectedItemCount={selectedItemCount}
          isDisabled={isLoading}
          confirmAction={purgeDialogConfirmAction}
          isOpen={isPurgeDialogOpen}
          onClose={handleClosePurgeDialog}
        />
      </TrashQuizActionProvider>
    </TrashFolderActionProvider>
  );
}
